import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * Global provider for:
 *  - Legal modals (Aviso Legal, Política de Privacidad, Política de Cookies)
 *  - Cookie consent state (accepted | rejected | undecided), persisted in localStorage
 *
 * The consent is used to gate any future non-essential cookies (analytics, ads, etc.).
 * Technical cookies (form CSRF, localStorage for the consent itself) are always allowed
 * per LSSI-CE exemption.
 */

const LegalContext = createContext(null);
const STORAGE_KEY = 'icb_cookie_consent_v1';

export const LEGAL_MODALS = {
  AVISO: 'aviso-legal',
  PRIVACIDAD: 'politica-privacidad',
  COOKIES: 'politica-cookies',
};

export const CONSENT = {
  UNDECIDED: 'undecided',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
};

export const LegalProvider = ({ children }) => {
  const [openModal, setOpenModal] = useState(null); // null | one of LEGAL_MODALS values
  const [consent, setConsentState] = useState(CONSENT.UNDECIDED);
  const [bannerVisible, setBannerVisible] = useState(false);

  // Load persisted consent on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === CONSENT.ACCEPTED || saved === CONSENT.REJECTED) {
        setConsentState(saved);
        setBannerVisible(false);
      } else {
        setBannerVisible(true);
      }
    } catch (e) {
      // localStorage may be disabled; show banner to be safe
      setBannerVisible(true);
    }
  }, []);

  const setConsent = useCallback((value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      // ignore
    }
    setConsentState(value);
    setBannerVisible(false);
  }, []);

  const acceptCookies = useCallback(() => setConsent(CONSENT.ACCEPTED), [setConsent]);
  const rejectCookies = useCallback(() => setConsent(CONSENT.REJECTED), [setConsent]);

  const showBanner = useCallback(() => setBannerVisible(true), []);
  const hideBanner = useCallback(() => setBannerVisible(false), []);

  const openLegalModal = useCallback((id) => setOpenModal(id), []);
  const closeLegalModal = useCallback(() => setOpenModal(null), []);

  return (
    <LegalContext.Provider
      value={{
        openModal,
        openLegalModal,
        closeLegalModal,
        consent,
        acceptCookies,
        rejectCookies,
        bannerVisible,
        showBanner,
        hideBanner,
      }}
    >
      {children}
    </LegalContext.Provider>
  );
};

export const useLegal = () => {
  const ctx = useContext(LegalContext);
  if (!ctx) throw new Error('useLegal must be used within a LegalProvider');
  return ctx;
};
